import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import GooglePlacesSearch from "components/basic/googlePlacesSearch";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import { useNavigate } from "react-router-dom";
import SignUpCardLayout from "../../components/SignUpCardLayout";
import { formatPhoneNumber, PHONE_NUMBER_PLACEHOLDER } from "pages/formTools";
import { Account } from "../..";
import { validateAddress } from "./validation";

interface AddressFormProps {
  signUpData: Account;
  setSignUpData: React.Dispatch<React.SetStateAction<Account>>;
}

const AddressForm: React.FC<AddressFormProps> = ({
  signUpData,
  setSignUpData,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");

  const { errors, validateAll, revalidate } = useFieldErrors(validateAddress);
  const handlePhoneNumberChange = useMaskedInput(formatPhoneNumber);

  // Applies an edit and re-checks any error already on screen.
  const updateField = (patch: Partial<Account>) => {
    const next = { ...signUpData, ...patch };
    setSignUpData(next);
    revalidate(next);
  };

  const handleNextClick = () => {
    if (!validateAll(signUpData)) {
      showSnack(t("address.fill_required"), "error");
      return;
    }

    navigate(
      signUpData.authProvider === "google"
        ? signUpData.userType === "user"
          ? "?currentPage=userReviewForm"
          : "?currentPage=businessInfoForm"
        : "?currentPage=emailVerificationForm",
    );
  };

  return (
    <SignUpCardLayout>
      <div
        id="right section"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <Typography
          variant="h4"
          align="center"
          style={{ margin: `${theme.spacing(4)} 0` }}
        >
          {t("common.create_your_account")}
        </Typography>

        <TextField
          size="tiny"
          name="phoneNumber"
          autoComplete="tel"
          value={signUpData.phoneNumber}
          placeHolder={t("address.phone_placeholder", {
            example: PHONE_NUMBER_PLACEHOLDER,
          })}
          textAlign="center"
          onChange={(event) =>
            updateField({
              phoneNumber: handlePhoneNumberChange(
                event,
                signUpData.phoneNumber,
              ),
            })
          }
          error={errors.phoneNumber ?? undefined}
        />

        <GooglePlacesSearch
          size="tiny"
          name="address"
          value={signUpData.address}
          textAlign="center"
          placeHolder={t(
            signUpData.userType === "user"
              ? "address.home_placeholder"
              : "address.business_placeholder",
          )}
          onInputChange={(text) =>
            // Free-typed text no longer matches a picked place — drop
            // any stale coordinates.
            updateField({
              address: text,
              latitude: null,
              longitude: null,
              placeId: null,
            })
          }
          onSelect={(place) =>
            updateField({
              address: place.address,
              latitude: place.latitude ?? null,
              longitude: place.longitude ?? null,
              placeId: place.placeId ?? null,
            })
          }
          error={errors.address ?? undefined}
        />
      </div>

      <div>
        <div
          style={{
            margin: `${theme.spacing(2)} 0`,
            marginTop: theme.spacing(6),
          }}
        >
          <Button
            onClick={handleNextClick}
            variant="contained"
            color="warning"
            size="small"
            style={{ color: theme.palette.common.white }}
            fullWidth
          >
            {t("common.next")}
          </Button>
        </div>

        <Button
          onClick={() => navigate("?currentPage=nameForm")}
          variant="contained"
          size="small"
          style={{ color: theme.palette.common.white }}
          fullWidth
        >
          {t("common.back")}
        </Button>
      </div>
    </SignUpCardLayout>
  );
};

export default AddressForm;
